import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import clientService from '../services/ClientService';
import serviceService from '../services/ServiceService';
import routerService from '../services/RouterService';

const emptyClient = {
    id: '',
    nombre: '',
    rif: '',
    cedula: '',
    direccion_principal: '',
    tipo_servicio: '',
    instalado: '',
    status: '',
    fecha_suspendido: '',
    correo: '',
    movil: '',
    telefono: '',
    coordenadas: '',
    coordenada: '',
    razon_social: '',
    default_service_id: '',
};

const emptyService = {
    id: '',
    plan: '',
    costo: '',
    ip: '',
    router_id: '',
    user_ppp_hotspot: '',
    password_ppp: '',
    instalado: '',
    direccion: '',
    estado: '',
};

const toDateInputValue = (value) => {
    if (!value) return '';
    // Acepta 'YYYY-MM-DD' o ISO y devuelve 'YYYY-MM-DD'
    return String(value).slice(0, 10);
};

const toDateTimeLocalValue = (value) => {
    if (!value) return '';
    // Acepta ISO 'YYYY-MM-DDTHH:mm:ss' y devuelve 'YYYY-MM-DDTHH:mm'
    const v = String(value);
    if (v.length >= 16) return v.slice(0, 16);
    return v;
};

const ClientEditPage = () => {
    const navigate = useNavigate();
    const { clientId } = useParams();

    const isNew = useMemo(() => clientId === 'new', [clientId]);

    const [activeTab, setActiveTab] = useState('resumen');
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    const [client, setClient] = useState(emptyClient);

    // Servicios
    const [services, setServices] = useState([]);
    const [servicesLoading, setServicesLoading] = useState(false);
    const [servicesError, setServicesError] = useState('');

    // Routers (para dropdown en servicios)
    const [routers, setRouters] = useState([]);
    const [routersLoading, setRoutersLoading] = useState(false);
    const [routersError, setRoutersError] = useState('');

    const [creatingService, setCreatingService] = useState(false);
    const [newService, setNewService] = useState(emptyService);

    const [editingService, setEditingService] = useState(null);

    const fetchClient = useCallback(async () => {
        if (isNew) {
            setClient(emptyClient);
            setLoading(false);
            return;
        }

        setLoading(true);
        setError('');
        try {
            const response = await clientService.getClient(clientId);
            const data = response.data;
            setClient({
                ...emptyClient,
                ...data,
                instalado: toDateInputValue(data.instalado),
                fecha_suspendido: toDateTimeLocalValue(data.fecha_suspendido),
                default_service_id: data.default_service_id ?? '',
            });
        } catch (err) {
            setError('No se pudo cargar el cliente.');
        } finally {
            setLoading(false);
        }
    }, [clientId, isNew]);

    const fetchServices = useCallback(async () => {
        if (isNew) return;
        setServicesLoading(true);
        setServicesError('');
        try {
            const response = await clientService.getClientServices(clientId);
            setServices(response.data);
        } catch (err) {
            setServicesError('No se pudo cargar la lista de servicios.');
        } finally {
            setServicesLoading(false);
        }
    }, [clientId, isNew]);

    useEffect(() => {
        fetchClient();
    }, [fetchClient]);

    useEffect(() => {
        if (activeTab === 'servicios' && !isNew) {
            fetchServices();
        }
    }, [activeTab, fetchServices, isNew]);

    const fetchRouters = useCallback(async () => {
        setRoutersLoading(true);
        setRoutersError('');
        try {
            const response = await routerService.getRouters();
            setRouters(response.data);
        } catch (err) {
            setRoutersError('No se pudo cargar la lista de routers.');
        } finally {
            setRoutersLoading(false);
        }
    }, []);

    useEffect(() => {
        if (activeTab === 'servicios') {
            fetchRouters();
        }
    }, [activeTab, fetchRouters]);

    const routersById = useMemo(() => {
        const map = new Map();
        routers.forEach((r) => map.set(r.id, r));
        return map;
    }, [routers]);

    const handleClientChange = (e) => {
        const { name, value } = e.target;
        setClient((prev) => ({ ...prev, [name]: value }));
    };

    const handleSaveClient = async (e) => {
        e.preventDefault();
        setSaving(true);
        setError('');

        const payload = {
            nombre: client.nombre,
            rif: client.rif || null,
            cedula: client.cedula || null,
            direccion_principal: client.direccion_principal || null,
            tipo_servicio: client.tipo_servicio || null,
            instalado: client.instalado || null,
            status: client.status || null,
            fecha_suspendido: client.fecha_suspendido || null,
            correo: client.correo || null,
            movil: client.movil || null,
            telefono: client.telefono || null,
            coordenadas: client.coordenadas || null,
            coordenada: client.coordenada || null,
            razon_social: client.razon_social || null,
            default_service_id: client.default_service_id || null,
        };

        try {
            if (isNew) {
                if (!client.id) {
                    setError('El ID es requerido.');
                    return;
                }
                const createPayload = { id: client.id, ...payload };
                await clientService.createClient(createPayload);
                navigate(`/clients/${client.id}`);
            } else {
                await clientService.updateClient(clientId, payload);
                await fetchClient();
            }
        } catch (err) {
            setError(err.response?.data?.detail || 'Error al guardar el cliente.');
        } finally {
            setSaving(false);
        }
    };

    // --- Servicios ---

    const handleNewServiceChange = (e) => {
        const { name, value } = e.target;
        setNewService((prev) => ({ ...prev, [name]: value }));
    };

    const handleCreateService = async (e) => {
        e.preventDefault();
        setServicesError('');

        if (!newService.id || !newService.plan) {
            setServicesError('ID y Plan son requeridos.');
            return;
        }

        const payload = {
            id: newService.id,
            client_id: clientId,
            plan: newService.plan,
            costo: newService.costo ? newService.costo : null,
            ip: newService.ip || null,
            router_id: newService.router_id ? parseInt(newService.router_id, 10) : null,
            user_ppp_hotspot: newService.user_ppp_hotspot || null,
            password_ppp: newService.password_ppp || null,
            instalado: newService.instalado || null,
            direccion: newService.direccion || null,
            estado: newService.estado || null,
        };

        try {
            await serviceService.createService(payload);
            setNewService(emptyService);
            setCreatingService(false);
            fetchServices();
        } catch (err) {
            setServicesError(err.response?.data?.detail || 'Error al crear el servicio.');
        }
    };

    const handleEditServiceClick = (service) => {
        setEditingService({
            ...emptyService,
            ...service,
            router_id: service.router_id ?? '',
            instalado: toDateInputValue(service.instalado),
            password_ppp: '',
        });
    };

    const handleEditServiceChange = (e) => {
        const { name, value } = e.target;
        setEditingService((prev) => ({ ...prev, [name]: value }));
    };

    const handleUpdateService = async (e) => {
        e.preventDefault();
        if (!editingService) return;

        const payload = {
            plan: editingService.plan,
            costo: editingService.costo ? editingService.costo : null,
            ip: editingService.ip || null,
            router_id: editingService.router_id ? parseInt(editingService.router_id, 10) : null,
            user_ppp_hotspot: editingService.user_ppp_hotspot || null,
            instalado: editingService.instalado || null,
            direccion: editingService.direccion || null,
            estado: editingService.estado || null,
        };

        if (editingService.password_ppp) {
            payload.password_ppp = editingService.password_ppp;
        }

        try {
            await serviceService.updateService(editingService.id, payload);
            setEditingService(null);
            fetchServices();
        } catch (err) {
            setServicesError('Error al actualizar el servicio.');
        }
    };

    const handleDeleteService = async (serviceId) => {
        if (window.confirm('¿Estás seguro de que quieres eliminar este servicio?')) {
            try {
                await serviceService.deleteService(serviceId);
                fetchServices();
            } catch (err) {
                setServicesError('Error al eliminar el servicio.');
            }
        }
    };

    return (
        <div className="container mx-auto p-4">
            <div className="flex items-center justify-between mb-4">
                <button
                    onClick={() => navigate('/clients')}
                    className="text-blue-600 hover:underline"
                >
                    ← Volver a clientes
                </button>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-md mb-4">
                <h1 className="text-2xl font-bold text-gray-800">
                    {isNew ? 'Nuevo cliente' : `${client.nombre || ''} (${clientId})`}
                </h1>
            </div>

            {/* Tabs */}
            <div className="bg-gray-800 text-white rounded-lg overflow-hidden mb-4">
                <div className="flex">
                    <button
                        onClick={() => setActiveTab('resumen')}
                        className={`px-4 py-2 text-sm font-semibold ${activeTab === 'resumen' ? 'bg-gray-900' : 'bg-gray-800 hover:bg-gray-700'}`}
                    >
                        Resumen
                    </button>
                    <button
                        onClick={() => setActiveTab('servicios')}
                        disabled={isNew}
                        className={`px-4 py-2 text-sm font-semibold ${activeTab === 'servicios' ? 'bg-gray-900' : 'bg-gray-800 hover:bg-gray-700'} ${isNew ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                        Servicios
                    </button>
                </div>
            </div>

            {activeTab === 'resumen' && (
                <div className="bg-white p-6 rounded-lg shadow-md">
                    {loading && <p>Cargando...</p>}
                    {error && <p className="text-red-500 mb-4">{error}</p>}

                    {!loading && (
                        <form onSubmit={handleSaveClient} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="md:col-span-2">
                                <h2 className="text-xl font-semibold text-gray-800">Datos del cliente</h2>
                            </div>

                            {isNew && (
                                <div>
                                    <label className="block text-sm font-bold mb-2">ID</label>
                                    <input
                                        name="id"
                                        value={client.id}
                                        onChange={handleClientChange}
                                        className="p-2 border rounded w-full"
                                        placeholder="Ej: 014073"
                                        required
                                    />
                                </div>
                            )}

                            <div>
                                <label className="block text-sm font-bold mb-2">Nombre</label>
                                <input
                                    name="nombre"
                                    value={client.nombre}
                                    onChange={handleClientChange}
                                    className="p-2 border rounded w-full"
                                    required
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-bold mb-2">RIF</label>
                                <input name="rif" value={client.rif} onChange={handleClientChange} className="p-2 border rounded w-full" />
                            </div>

                            <div>
                                <label className="block text-sm font-bold mb-2">Cédula</label>
                                <input name="cedula" value={client.cedula} onChange={handleClientChange} className="p-2 border rounded w-full" />
                            </div>

                            <div>
                                <label className="block text-sm font-bold mb-2">Router</label>
                                <p className="text-sm text-gray-600">Se asigna desde la pestaña <b>Servicios</b>.</p>
                            </div>

                            <div className="md:col-span-2">
                                <label className="block text-sm font-bold mb-2">Dirección principal</label>
                                <input name="direccion_principal" value={client.direccion_principal} onChange={handleClientChange} className="p-2 border rounded w-full" />
                            </div>

                            <div>
                                <label className="block text-sm font-bold mb-2">Tipo de servicio</label>
                                <input name="tipo_servicio" value={client.tipo_servicio} onChange={handleClientChange} className="p-2 border rounded w-full" />
                            </div>

                            <div>
                                <label className="block text-sm font-bold mb-2">Instalado</label>
                                <input name="instalado" type="date" value={client.instalado} onChange={handleClientChange} className="p-2 border rounded w-full" />
                            </div>

                            <div>
                                <label className="block text-sm font-bold mb-2">Status</label>
                                <input name="status" value={client.status} onChange={handleClientChange} className="p-2 border rounded w-full" placeholder="ONLINE/OFFLINE/ACTIVO" />
                            </div>

                            <div>
                                <label className="block text-sm font-bold mb-2">Fecha suspendido</label>
                                <input name="fecha_suspendido" type="datetime-local" value={client.fecha_suspendido} onChange={handleClientChange} className="p-2 border rounded w-full" />
                            </div>

                            <div>
                                <label className="block text-sm font-bold mb-2">Correo</label>
                                <input name="correo" value={client.correo} onChange={handleClientChange} className="p-2 border rounded w-full" />
                            </div>

                            <div>
                                <label className="block text-sm font-bold mb-2">Móvil</label>
                                <input name="movil" value={client.movil} onChange={handleClientChange} className="p-2 border rounded w-full" />
                            </div>

                            <div>
                                <label className="block text-sm font-bold mb-2">Teléfono</label>
                                <input name="telefono" value={client.telefono} onChange={handleClientChange} className="p-2 border rounded w-full" />
                            </div>

                            <div>
                                <label className="block text-sm font-bold mb-2">Coordenadas</label>
                                <input name="coordenadas" value={client.coordenadas} onChange={handleClientChange} className="p-2 border rounded w-full" placeholder="lat, lng" />
                            </div>

                            <div>
                                <label className="block text-sm font-bold mb-2">Coordenada</label>
                                <input name="coordenada" value={client.coordenada} onChange={handleClientChange} className="p-2 border rounded w-full" />
                            </div>

                            <div>
                                <label className="block text-sm font-bold mb-2">Razón social</label>
                                <input name="razon_social" value={client.razon_social} onChange={handleClientChange} className="p-2 border rounded w-full" />
                            </div>

                            <div>
                                <label className="block text-sm font-bold mb-2">Servicio principal (ID)</label>
                                <input name="default_service_id" value={client.default_service_id} onChange={handleClientChange} className="p-2 border rounded w-full" placeholder="Ej: 14195" />
                            </div>

                            <div className="md:col-span-2 flex justify-end mt-2">
                                <button
                                    type="submit"
                                    disabled={saving}
                                    className="bg-blue-500 text-white py-2 px-4 rounded hover:bg-blue-600 disabled:bg-gray-400"
                                >
                                    {saving ? 'Guardando...' : (isNew ? 'Crear cliente' : 'Guardar cambios')}
                                </button>
                            </div>
                        </form>
                    )}
                </div>
            )}

            {activeTab === 'servicios' && (
                <div className="bg-white p-6 rounded-lg shadow-md">
                    <div className="flex items-center justify-between mb-4">
                        <h2 className="text-xl font-semibold text-gray-800">Servicios</h2>
                        <button
                            onClick={() => {
                                setCreatingService((v) => !v);
                                setNewService(emptyService);
                                setServicesError('');
                            }}
                            className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
                        >
                            {creatingService ? 'Cancelar' : 'Nuevo'}
                        </button>
                    </div>

                    {servicesError && <p className="text-red-500 mb-4">{servicesError}</p>}
                    {routersError && <p className="text-red-500 mb-4">{routersError}</p>}

                    {creatingService && (
                        <form onSubmit={handleCreateService} className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-gray-50 p-4 rounded mb-6">
                            <div>
                                <label className="block text-sm font-bold mb-2">ID</label>
                                <input name="id" value={newService.id} onChange={handleNewServiceChange} className="p-2 border rounded w-full" placeholder="Ej: 14195" required />
                            </div>
                            <div>
                                <label className="block text-sm font-bold mb-2">Plan</label>
                                <input name="plan" value={newService.plan} onChange={handleNewServiceChange} className="p-2 border rounded w-full" placeholder="Ej: SUPER 400MB" required />
                            </div>
                            <div>
                                <label className="block text-sm font-bold mb-2">Costo</label>
                                <input name="costo" value={newService.costo} onChange={handleNewServiceChange} className="p-2 border rounded w-full" placeholder="30.00" />
                            </div>
                            <div>
                                <label className="block text-sm font-bold mb-2">IP</label>
                                <input name="ip" value={newService.ip} onChange={handleNewServiceChange} className="p-2 border rounded w-full" placeholder="10.102.0.10" />
                            </div>
                            <div>
                                <label className="block text-sm font-bold mb-2">Router</label>
                                <div className="flex gap-2">
                                    <select
                                        name="router_id"
                                        value={newService.router_id}
                                        onChange={handleNewServiceChange}
                                        className="p-2 border rounded w-full"
                                        disabled={routersLoading}
                                    >
                                        <option value="">Seleccionar router…</option>
                                        {routers.map((r) => (
                                            <option key={r.id} value={r.id}>{r.nombre_router} (ID: {r.id})</option>
                                        ))}
                                    </select>
                                    <button
                                        type="button"
                                        onClick={() => navigate('/routers')}
                                        className="bg-gray-800 text-white px-3 py-2 rounded hover:bg-gray-700"
                                        title="Crear un router si no existe"
                                    >
                                        Crear router
                                    </button>
                                </div>
                            </div>
                            <div>
                                <label className="block text-sm font-bold mb-2">User PPP/Hotspot</label>
                                <input name="user_ppp_hotspot" value={newService.user_ppp_hotspot} onChange={handleNewServiceChange} className="p-2 border rounded w-full" />
                            </div>
                            <div>
                                <label className="block text-sm font-bold mb-2">Password/PPP</label>
                                <input name="password_ppp" type="password" value={newService.password_ppp} onChange={handleNewServiceChange} className="p-2 border rounded w-full" />
                            </div>
                            <div>
                                <label className="block text-sm font-bold mb-2">Instalado</label>
                                <input name="instalado" type="date" value={newService.instalado} onChange={handleNewServiceChange} className="p-2 border rounded w-full" />
                            </div>
                            <div>
                                <label className="block text-sm font-bold mb-2">Dirección</label>
                                <input name="direccion" value={newService.direccion} onChange={handleNewServiceChange} className="p-2 border rounded w-full" />
                            </div>
                            <div>
                                <label className="block text-sm font-bold mb-2">Estado</label>
                                <input name="estado" value={newService.estado} onChange={handleNewServiceChange} className="p-2 border rounded w-full" placeholder="ONLINE/OFFLINE" />
                            </div>
                            <div className="md:col-span-2 flex justify-end">
                                <button type="submit" className="bg-blue-500 text-white py-2 px-4 rounded hover:bg-blue-600">Crear servicio</button>
                            </div>
                        </form>
                    )}

                    <div className="overflow-x-auto">
                        <table className="min-w-full bg-white">
                            <thead className="bg-gray-100">
                                <tr>
                                    <th className="py-2 px-4 text-left">ID</th>
                                    <th className="py-2 px-4 text-left">Plan</th>
                                    <th className="py-2 px-4 text-left">Costo</th>
                                    <th className="py-2 px-4 text-left">IP</th>
                                    <th className="py-2 px-4 text-left">Router</th>
                                    <th className="py-2 px-4 text-left">Dirección</th>
                                    <th className="py-2 px-4 text-left">Estado</th>
                                    <th className="py-2 px-4 text-left">Acciones</th>
                                </tr>
                            </thead>
                            <tbody>
                                {servicesLoading ? (
                                    <tr><td colSpan="8" className="text-center py-6">Cargando...</td></tr>
                                ) : services.length === 0 ? (
                                    <tr><td colSpan="8" className="text-center py-6 text-gray-500">No hay servicios</td></tr>
                                ) : (
                                    services.map((s) => (
                                        <tr key={s.id} className="border-b hover:bg-gray-50">
                                            <td className="py-2 px-4">{s.id}</td>
                                            <td className="py-2 px-4">{s.plan}</td>
                                            <td className="py-2 px-4">{s.costo ?? ''}</td>
                                            <td className="py-2 px-4">{s.ip ?? ''}</td>
                                            <td className="py-2 px-4">{(s.router_id && routersById.get(s.router_id)?.nombre_router) ? routersById.get(s.router_id).nombre_router : (s.router_id ?? '')}</td>
                                            <td className="py-2 px-4">{s.direccion ?? ''}</td>
                                            <td className="py-2 px-4">{s.estado ?? ''}</td>
                                            <td className="py-2 px-4">
                                                <button
                                                    onClick={() => handleEditServiceClick(s)}
                                                    className="bg-yellow-500 text-white px-3 py-1 rounded text-sm mr-2 hover:bg-yellow-600"
                                                >
                                                    Editar
                                                </button>
                                                <button
                                                    onClick={() => handleDeleteService(s.id)}
                                                    className="bg-red-500 text-white px-3 py-1 rounded text-sm hover:bg-red-600"
                                                >
                                                    Eliminar
                                                </button>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>

                    {editingService && (
                        <div className="mt-6 bg-gray-50 p-4 rounded">
                            <h3 className="text-lg font-semibold text-gray-800 mb-4">Editar servicio ({editingService.id})</h3>
                            <form onSubmit={handleUpdateService} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-bold mb-2">Plan</label>
                                    <input name="plan" value={editingService.plan} onChange={handleEditServiceChange} className="p-2 border rounded w-full" required />
                                </div>
                                <div>
                                    <label className="block text-sm font-bold mb-2">Costo</label>
                                    <input name="costo" value={editingService.costo} onChange={handleEditServiceChange} className="p-2 border rounded w-full" />
                                </div>
                                <div>
                                    <label className="block text-sm font-bold mb-2">IP</label>
                                    <input name="ip" value={editingService.ip} onChange={handleEditServiceChange} className="p-2 border rounded w-full" />
                                </div>
                                <div>
                                    <label className="block text-sm font-bold mb-2">Router</label>
                                    <div className="flex gap-2">
                                        <select
                                            name="router_id"
                                            value={editingService.router_id}
                                            onChange={handleEditServiceChange}
                                            className="p-2 border rounded w-full"
                                            disabled={routersLoading}
                                        >
                                            <option value="">Seleccionar router…</option>
                                            {routers.map((r) => (
                                                <option key={r.id} value={r.id}>{r.nombre_router} (ID: {r.id})</option>
                                            ))}
                                        </select>
                                        <button
                                            type="button"
                                            onClick={() => navigate('/routers')}
                                            className="bg-gray-800 text-white px-3 py-2 rounded hover:bg-gray-700"
                                            title="Crear un router si no existe"
                                        >
                                            Crear router
                                        </button>
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-sm font-bold mb-2">User PPP/Hotspot</label>
                                    <input name="user_ppp_hotspot" value={editingService.user_ppp_hotspot} onChange={handleEditServiceChange} className="p-2 border rounded w-full" />
                                </div>
                                <div>
                                    <label className="block text-sm font-bold mb-2">Nueva Password (opcional)</label>
                                    <input name="password_ppp" type="password" value={editingService.password_ppp} onChange={handleEditServiceChange} className="p-2 border rounded w-full" placeholder="Dejar en blanco para no cambiar" />
                                </div>
                                <div>
                                    <label className="block text-sm font-bold mb-2">Instalado</label>
                                    <input name="instalado" type="date" value={editingService.instalado} onChange={handleEditServiceChange} className="p-2 border rounded w-full" />
                                </div>
                                <div className="md:col-span-2">
                                    <label className="block text-sm font-bold mb-2">Dirección</label>
                                    <input name="direccion" value={editingService.direccion} onChange={handleEditServiceChange} className="p-2 border rounded w-full" />
                                </div>
                                <div>
                                    <label className="block text-sm font-bold mb-2">Estado</label>
                                    <input name="estado" value={editingService.estado} onChange={handleEditServiceChange} className="p-2 border rounded w-full" />
                                </div>
                                <div className="md:col-span-2 flex justify-end mt-2">
                                    <button type="button" onClick={() => setEditingService(null)} className="bg-gray-500 text-white py-2 px-4 rounded mr-2 hover:bg-gray-600">Cancelar</button>
                                    <button type="submit" className="bg-blue-500 text-white py-2 px-4 rounded hover:bg-blue-600">Guardar cambios</button>
                                </div>
                            </form>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default ClientEditPage;
